import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import { WorkspaceGridSavedViewService } from '@helios/user-preferences'
import { getToday } from '@helios/utils'
import { toDisplayFormat, toISOFormat } from '@northfork/basics'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { FileManagerNodeGridKey } from '../../../panel-key'

@Component({
  selector: 'hs-external-file-manager-header[accountId][reconDate]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./external-file-manager-header.component.scss'],
  templateUrl: './external-file-manager-header.component.html',
})
export class ExternalFileManagerHeaderComponent implements OnInit, OnDestroy {
  private readonly _destroyed$ = new Subject<void>()
  minDate = '1900-01-01'
  maxDate = '2100-12-31'

  constructor(
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  @Input() set accountId(id: number | null) {
    if (id == null) {
      this.accountIdDisplay = ''
      return
    }
    this.accountIdDisplay = `Account ${id}`
  }

  @Input() set reconDate(reconDate: string | null) {
    if (reconDate == null || this.dateControl.value != null) {
      return
    }
    this.setDateControl(reconDate)
  }

  @Output() selectedDateChange = new EventEmitter<string>()

  accountIdDisplay = ''
  // This should always be an ISO date string
  readonly dateControl = new FormControl(null, [Validators.required])
  readonly executionNumberControl = new FormControl()

  async ngOnInit() {
    this.dateControl.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        filter(() => this.dateControl.valid),
      )
      .subscribe((date: string) => {
        this.savedViewService.updatePanelSettingsOnly(FileManagerNodeGridKey, {
          key: 'selectedDate',
          value: date,
        })
        this.selectedDateChange.emit(date)
      })

    const panelSettings = (await this.savedViewService.getSavedPanelSettings(
      FileManagerNodeGridKey,
    )) as { [key: string]: unknown }
    if ('selectedDate' in panelSettings) {
      this.dateControl.setValue(panelSettings.selectedDate)
    }
  }

  ngOnDestroy() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /** Get date control value as a display date string */
  getDateControlDisplay(): string {
    return toDisplayFormat(this.dateControl.value) as string
  }

  /** Set the date control with a date object */
  setDateControl(date: string) {
    // Date control must always be an ISO string
    this.dateControl.setValue(date)
  }

  onMoveDateBackward() {
    const date = new Date(this.getDateControlDisplay())
    date.setDate(date.getDate() - 1)
    this.setDateControl(toISOFormat(date) as string)
  }

  onMoveDateForward() {
    const date = new Date(this.getDateControlDisplay())
    date.setDate(date.getDate() + 1)
    this.setDateControl(toISOFormat(date) as string)
  }

  onPriorClicked() {
    const date = new Date(getToday())
    const day = date.getDay()
    // prior date on sunday and monday is previous friday, otherwise the day before
    const diff = day == 0 || day == 1 ? day + 2 : 1
    date.setDate(date.getDate() - diff)
    this.setDateControl(toISOFormat(date) as string)
  }

  onTodayClicked() {
    this.setDateControl(toISOFormat(getToday()) as string)
  }
}
